<script setup lang="ts">
  import type { ClpBannerStoryblok } from '@/storyblok/component-types-sb'
  import { ASSET_TYPE_IMAGE } from '@/utils/constants/media'
  import type { ICampaignDates } from '@/components/campaign/banner/types'
  import { cva } from 'class-variance-authority'
  import {
    VARIATION_ASSET_IN_BANNER,
    VARIATION_BANNER_ONLY,
    VARIATION_SEPARATE_ASSET_BANNER
  } from '@/utils/constants/clp'
  import { useStoryblokAssetData } from '@/composables/useStoryblokAssetData'
  import { marked } from 'marked'
  import { optionalString } from '@/utils/storyblok/normalizers'

  const props = defineProps<{
    blok: ClpBannerStoryblok
  }>()

  const counterBase = computed(() =>
    cva('absolute -top-22 flex w-full justify-center md:-top-22', {
      variants: {
        theme: {
          green: 'text-bt-blue-dark',
          purple: 'text-white',
          blue: 'text-white'
        }
      }
    })({
      theme: props.blok.color_theme || 'green'
    })
  )

  const { isTablet, isDesktop } = useBTBreakpoints()
  const { getAssetData } = useStoryblokAssetData()

  const activeAsset = computed(() => {
    if (isDesktop.value) {
      return getAssetData(props.blok.desktop_asset, 520)
    }

    if (isTablet.value) {
      return getAssetData(props.blok.tablet_asset, 360)
    }

    return getAssetData(props.blok.mobile_asset, 240)
  })

  const blokVariation = computed(() => props.blok.variation)

  const campaignDates = inject<ICampaignDates>('campaignDates')

  const campaignBannerData = computed(() => ({
    title: props.blok.title,
    showDynamicCheapestPriceInTitle: props.blok.showDynamicCheapestPriceInTitle,
    color_theme: props.blok.color_theme,
    book_date_start: campaignDates?.book_date_start,
    book_date_end: campaignDates?.book_date_end,
    travel_date_start: campaignDates?.travel_date_start,
    travel_date_end: campaignDates?.travel_date_end,
    show_timer: campaignDates?.show_timer
  }))

  const bgImageStyle = computed(
    () =>
      `background: url('${activeAsset.value.src}') center center / cover no-repeat; height: ${activeAsset.value.height}px;`
  )

  const isActiveAssetImage = computed(() => activeAsset.value.assetType === ASSET_TYPE_IMAGE)
  const isBannerVariationSeparateAsset = computed(() => blokVariation.value === VARIATION_SEPARATE_ASSET_BANNER)

  const showTimer = computed(() => campaignBannerData.value.show_timer)
  const descriptionText = computed(() => marked(props?.blok?.description ?? ''))
</script>
<template>
  <div class="mb-12 md:mb-18">
    <template v-if="isBannerVariationSeparateAsset || blokVariation === VARIATION_BANNER_ONLY">
      <LazyCampaignBannerLayoutStacked :data="campaignBannerData">
        <template v-if="activeAsset?.filename && isBannerVariationSeparateAsset">
          <div
            v-if="isActiveAssetImage"
            class="relative w-full"
            data-test="bg-cover"
            :style="bgImageStyle"
          />
          <video
            v-else
            class="w-full"
            :style="`height: ${activeAsset.height}px; object-fit: cover; object-position: center;`"
            :src="activeAsset.src"
            :aria-label="optionalString(activeAsset.alt)"
            autoplay
            loop
            muted
            playsinline
            webkit-playsinline
          />
        </template>
      </LazyCampaignBannerLayoutStacked>
      <div
        v-if="showTimer"
        class="relative"
      >
        <div :class="counterBase">
          <LazyCampaignCounter
            :start-date="campaignBannerData.book_date_start"
            :end-date="campaignBannerData.book_date_end"
          />
        </div>
      </div>
    </template>
    <LazyCampaignBannerLayoutFullWidth
      v-if="blokVariation === VARIATION_ASSET_IN_BANNER"
      :data="campaignBannerData"
    >
      <div
        v-if="isActiveAssetImage"
        class="relative w-full"
        data-test="bg-cover"
        :style="bgImageStyle"
      />
      <video
        v-else
        class="size-full"
        :style="`object-fit: cover; object-position: center;`"
        :src="activeAsset.src"
        :aria-label="optionalString(activeAsset.alt)"
        autoplay
        loop
        muted
        playsinline
        webkit-playsinline
      />
    </LazyCampaignBannerLayoutFullWidth>
    <div
      v-if="(blok?.description ?? '').length > 0"
      class="container mt-20 text-left md:mt-22 md:text-center lg:max-w-186"
      v-html="descriptionText"
    />
  </div>
</template>
<style lang="scss" scoped>
  video {
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: manipulation;
  }
</style>
